import { AfterViewInit, Component, inject, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { EditClientComponent } from './edit-client.component';
import {
  SynAlertComponent,
  SynBreadcrumbComponent,
  SynBreadcrumbItemComponent, SynButtonComponent, SynIconComponent, SynSpinnerComponent,
  SynTabComponent,
  SynTabGroupComponent,
  SynTabPanelComponent,
} from '@synergy-design-system/angular';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SynTabShowEvent } from '@synergy-design-system/components';
import { ClientRolesComponent } from './client-roles.component';
import { ServicePortalApi } from '../../../api/api';
import {
  Client,
  ClientDetails,
  ClientSecrets,
  Permission,
  Role,
  RoleMapper,
  TestUser,
  TokenMapper,
} from '../../../api/types';
import { ClientRoleMappers, LocalManagementRoleMember } from '../../types';
import { globalClientId } from '../../state';
import { ClientPermissionsComponent } from './client-permisions.component';
import { TokenMappersComponent } from './token-mappers.component';
import { RoleMappersComponent } from './role-mappers.component';
import { handleError } from '../../../errors';
import { ClientCredentialsComponent as ClientCredentialsComponent } from './client-credentials.component';
import { ScopeEvaluationComponent } from './scope-evaluation.component';
import { ClientTestUsersComponent } from './client-test-users.component';
import { StateService } from '../../../api/state';

type ClientTab =
  'settings' | 'roles' | 'credentials' | 'mappers' | 'role-mappers' | 'token-preview' | 'test-users' | 'permissions';

function isClientTab(value: string | undefined): value is ClientTab {
  switch (value) {
    case 'settings':
    case 'credentials':
    case 'roles':
    case 'mappers':
    case 'role-mappers':
    case 'token-preview':
    case 'test-users':
    case 'permissions':
      return true;

    default:
      return false;
  }
}

@Component({
  selector: 'app-client-settings',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/clients" i18n="@@clients">Clients</syn-breadcrumb-item>
      <syn-breadcrumb-item>{{ client()?.clientId ?? "" }}</syn-breadcrumb-item>
    </syn-breadcrumb>

    <syn-alert variant="warning" [open]="client() !== null && !client()?.enabled" class="margin-top-1 margin-bottom-1">
      <syn-icon name="warning_fill" slot="icon" />

      <div class="flex-col gap-1">
        <span i18n="@@client-deactivation-hint">
          This client is deactivated. If you no longer need this client, please delete it.
          If you still need this client, reactivate it and make sure to regularly login to the client.
        </span>

        <div>
          <syn-button size="small" variant="filled" (click)="onReactivateClient()">
            <syn-icon name="lock_open_fill" slot="prefix" />
            <span i18n="@@reactivate-client">Reactivate Client</span>
          </syn-button>
        </div>
      </div>
    </syn-alert>

    <syn-tab-group (synTabShowEvent)="onShowTab($event)">
      <syn-tab [disabled]="!showGeneralTabs()"
               [active]="tab() === 'settings'"
               slot="nav" panel="settings">
        <span i18n="@@settings">Settings</span>
      </syn-tab>
      <syn-tab [disabled]="!showGeneralTabs()"
               [active]="tab() === 'roles'"
               slot="nav" panel="roles"
               data-e2e="tab-roles">
        <span i18n="@@roles">Roles</span>
      </syn-tab>

      <syn-tab [disabled]="!showAdminTabs()"
               [active]="tab() === 'credentials'"
               slot="nav" panel="credentials"
               data-e2e="tab-credentials">
        <span i18n="@@client-auth-credentials">Credentials</span>
      </syn-tab>
      <syn-tab [disabled]="!showAdminTabs()"
               [active]="tab() === 'mappers'"
               slot="nav" panel="mappers"
               data-e2e="tab-mappers">
        <span i18n="@@token-mappers">Token Mappers</span>
      </syn-tab>
      <syn-tab [disabled]="!showAdminTabs()"
               [active]="tab() === 'role-mappers'"
               slot="nav" panel="role-mappers"
                data-e2e="tab-role-mappers">
        <span i18n="@@role-mappers">Role Mappers</span>
      </syn-tab>
      <syn-tab [disabled]="!showAdminTabs()"
               [active]="tab() === 'token-preview'"
               slot="nav" panel="token-preview"
               data-e2e="tab-token-preview">
        <span i18n="@@token-preview">Token Preview</span>
      </syn-tab>
      <syn-tab [disabled]="!showAdminTabs()"
               [active]="tab() === 'test-users'"
               slot="nav" panel="test-users"
               data-e2e="tab-test-users">
        <span i18n="@@test-users">Test Users</span>
      </syn-tab>
      <syn-tab [disabled]="!showAdminTabs()"
               [active]="tab() === 'permissions'"
               slot="nav" panel="permissions"
               data-e2e="tab-permissions">
        <span i18n="@@permissions">Permissions</span>
      </syn-tab>

      <syn-tab-panel name="settings"
                     [active]="tab() === 'settings'">
        @if (client()) {
          <app-edit-client
            [client]="client()!"
            [canEdit]="canEditSettings()"
            (changed)="reloadClientSecrets()"
          />
        } @else {
          <syn-spinner />
        }
      </syn-tab-panel>
      <syn-tab-panel name="roles"
                     [active]="tab() === 'roles'">
        @if (showGeneralTabs()) {
          <app-client-roles [roles]="roles()" [canManageRoles]="canManageRoles()" />
        }
      </syn-tab-panel>
      <syn-tab-panel name="credentials"
                     [active]="tab() === 'credentials'">
        @if (client() && clientSecrets() && showAdminTabs()) {
          <app-client-credentials [client]="client()!" [clientSecrets]="clientSecrets()!" />
        }
      </syn-tab-panel>

      <syn-tab-panel name="mappers"
                     [active]="tab() === 'mappers'">
        @if (showAdminTabs()) {
          <app-token-mappers [mappers]="mappers()!" />
        }
      </syn-tab-panel>
      <syn-tab-panel name="role-mappers"
                     [active]="tab() === 'role-mappers'">
        @if (showAdminTabs()) {
          <app-role-mappers [available]="available()!" />
        }
      </syn-tab-panel>
      <syn-tab-panel name="token-preview"
                     [active]="tab() === 'token-preview'">
        @if (showAdminTabs()) {
          <app-client-scope-evaluation />
        }
      </syn-tab-panel>
      <syn-tab-panel name="test-users"
                     [active]="tab() === 'test-users'"
      >
        @if (showAdminTabs()) {
          <app-client-test-users
            [users]="testUsers()"
            [clientId]="client()?.id ?? ''"
          />
        }
      </syn-tab-panel>
      <syn-tab-panel name="permissions"
                     [active]="tab() === 'permissions'">
        @if (adminRoles() && showAdminTabs()) {
          <app-client-permissions [adminRoles]="adminRoles()!" [(members)]="members" />
        }
      </syn-tab-panel>
    </syn-tab-group>
  `,
  styles: [``],
  imports: [
    EditClientComponent,
    ClientRolesComponent,
    SynTabGroupComponent,
    SynTabPanelComponent,
    SynTabComponent,
    RouterLink,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    ClientPermissionsComponent,
    TokenMappersComponent,
    RoleMappersComponent,
    ClientCredentialsComponent,
    ScopeEvaluationComponent,
    ClientTestUsersComponent,
    SynSpinnerComponent,
    SynAlertComponent,
    SynButtonComponent,
    SynIconComponent,
  ],
})
export class ClientSettingsComponent implements OnInit, AfterViewInit {
  router = inject(Router);
  route = inject(ActivatedRoute);
  api = inject(ServicePortalApi);
  state = inject(StateService);

  id = this.route.snapshot.params['id'];

  client = signal<Client | null>(null);
  clientSecrets = signal<ClientSecrets | null>(null);
  roles = signal<Role[]>([]);
  mappers = signal<TokenMapper[]>([]);
  available = signal<ClientRoleMappers[]>([]);
  testUsers = signal<TestUser[]>([]);

  members: WritableSignal<LocalManagementRoleMember[]> = signal([]);
  adminRoles: WritableSignal<Permission[]> = signal([]);

  showGeneralTabs = signal(true);
  showAdminTabs = signal(false);
  tab = signal<ClientTab>('settings');

  canEditSettings = signal(false);
  canManageRoles = signal(false);

  @ViewChild(SynTabGroupComponent) tabGroup!: SynTabGroupComponent;

  ngOnInit() {
    this.route.params.subscribe((params) => {
      const tab = params['tab'] ?? 'settings';
      if (isClientTab(tab)) {
        this.tab.set(tab);
      } else {
        console.warn('Unknown tab parameter', tab);
      }
    });

    // If the user navigated from the client list, we already have the client settings
    // So we can display them already, while we load the roles, mappers, etc.
    const client = this.state.lookupClient(this.id);
    this.client.set(client);

    this.api.getClientDetails(
      this.id,
      (value) => {
        this.mapPermission(value.client.associatedRole);
        this.getClientInfo(value);

        value.client.redirectUris.sort();
        value.client.webOrigins.sort();

        this.client.set(value.client);
        globalClientId.set(value.client.clientId);
      },
      (error) => {
        this.mapPermission('none');
        handleError(error);
      },
    );
  }

  ngAfterViewInit() {
    const tab = this.route.snapshot.params['tab'] ?? 'settings';
    if (isClientTab(tab)) {
      this.tabGroup.nativeElement.show(tab);
    } else {
      console.warn('Unknown tab parameter', tab);
    }
  }

  onShowTab(event: SynTabShowEvent) {
    const synEvent = event as SynTabShowEvent;
    const tab = synEvent.detail.name;
    if (isClientTab(tab)) {
      this.tab.set(tab);
    }

    void this.router.navigate(['clients', this.id, tab]).then(() => {
    });
  }

  mapPermission(permissionRole: string) {
    this.showGeneralTabs.set(permissionRole !== 'none');
    this.showAdminTabs.set(permissionRole === 'client_admin');

    // set edit / manage signals
    switch (permissionRole) {
      case 'client_admin':
        this.canEditSettings.set(true);
        this.canManageRoles.set(true);
        break;
      case 'role_admin':
        this.canManageRoles.set(true);
        break;
    }
  }

  reloadClientSecrets() {
    if (this.showAdminTabs()) {
      this.api.getClientSecrets(this.id, (value) => {
        this.clientSecrets.set(value);
      });
    }
  }

  getClientInfo(details: ClientDetails) {
    this.clientSecrets.set(details.secrets ?? null);
    this.roles.set(details.roles.roles);
    this.mappers.set(details?.mappers?.mappers ?? []);
    this.testUsers.set(details.testUsers?.users ?? []);

    if (details.roleMappers) {
      const setMappers = details.roleMappers.mappers;
      const isMapperSet = (mapper: RoleMapper) => {
        for (const setMapper of setMappers) {
          if (setMapper.clientId === mapper.clientId && setMapper.roleId === mapper.roleId) {
            return true;
          }
        }
        return false;
      };

      const availableMappers: ClientRoleMappers[] = [];
      let currentMapper: ClientRoleMappers | null = null;
      for (const mapper of details.roleMappers.available) {
        if (!currentMapper || currentMapper.clientId !== mapper.clientId) {
          currentMapper = {
            clientId: mapper.clientId,
            available: [],
            model: [],
          };
          availableMappers.push(currentMapper);
        }
        currentMapper.available.push(mapper);
        if (isMapperSet(mapper)) {
          currentMapper.model.push(mapper.roleId);
        }
      }
      this.available.set(availableMappers);
    }

    if (details.permissions) {
      this.adminRoles.set(details.permissions.permissionRoles);
      const members: LocalManagementRoleMember[] = [];
      for (const permissionRole of details.permissions.permissionRoles) {
        for (const member of permissionRole.members) {
          const mgmtMember = member as LocalManagementRoleMember;
          mgmtMember.mgmtRole = permissionRole.name;
          mgmtMember.oldMgmtRole = permissionRole.name;
          members.push(mgmtMember);
        }
      }
      this.members.set(members);
    }
  }

  onReactivateClient() {
    const client = this.client();
    if (client == null) return;

    const newClient = { ...client, enabled: true };
    this.api.updateClient(this.id, newClient, () => {
      this.client.set(newClient);
    });
  }
}
